import { Code, File, FileText, Upload, X } from 'lucide-react';
import type React from 'react';
import { useCallback, useState } from 'react';
import SparkMD5 from 'spark-md5';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

interface UploadedFile {
  id: string;
  filename: string;
  size: number;
  type: string;
  status: 'uploading' | 'uploaded' | 'error';
  progress: number;
}

interface FileUploaderProps {
  onFilesUploaded: (fileIds: string[]) => void;
  maxFiles?: number;
  acceptedTypes?: string[];
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  onFilesUploaded,
  maxFiles = 10,
  acceptedTypes = ['.md', '.json', '.yaml', '.yml', '.txt', '.pdf']
}) => {
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    
    const droppedFiles = Array.from(e.dataTransfer.files);
    void handleFiles(droppedFiles);
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const selectedFiles = Array.from(e.target.files);
      void handleFiles(selectedFiles);
      // Reset the input value to allow selecting the same file again
      e.target.value = '';
    }
  };

  // Helper function to calculate MD5 checksum for Active Storage
  const calculateChecksum = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      const spark = new SparkMD5.ArrayBuffer();
      
      reader.onload = (e) => {
        if (e.target?.result) {
          spark.append(e.target.result as ArrayBuffer);
          const hexHash = spark.end();
          // Convert hex to base64 (Rails expects base64-encoded MD5)
          const base64 = btoa(hexHash.match(/\w{2}/g)!.map(a => String.fromCharCode(parseInt(a, 16))).join(''));
          resolve(base64);
        }
      };
      
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });
  };

  const handleFiles = async (newFiles: File[]) => {
    if (files.length + newFiles.length > maxFiles) {
      alert(`Maximum ${maxFiles} files allowed`);
      return;
    }

    const uploadedFileIds: string[] = [];

    for (const file of newFiles) {
      const tempId = `temp-${Date.now()}-${Math.random()}`;
      const uploadedFile: UploadedFile = {
        id: tempId,
        filename: file.name,
        size: file.size,
        type: file.type,
        status: 'uploading',
        progress: 0
      };

      setFiles(prev => [...prev, uploadedFile]);

      try {
        // Calculate checksum for the file
        const checksum = await calculateChecksum(file);

        // Step 1: Request direct upload URL from Rails
        const directUploadResponse = await fetch('/rails/active_storage/direct_uploads', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
          },
          body: JSON.stringify({
            blob: {
              filename: file.name,
              content_type: file.type || 'application/octet-stream',
              byte_size: file.size,
              checksum: checksum
            }
          })
        });

        if (!directUploadResponse.ok) throw new Error('Failed to get upload URL');

        const directUploadData = await directUploadResponse.json() as {
          signed_id: string;
          direct_upload: {
            url: string;
            headers: Record<string, string>;
          };
        };

        // Step 2: Upload file directly to storage
        const uploadResponse = await fetch(directUploadData.direct_upload.url, {
          method: 'PUT',
          headers: directUploadData.direct_upload.headers,
          body: file
        });

        if (!uploadResponse.ok) throw new Error('Direct upload failed');

        setFiles(prev => prev.map(f => 
          f.id === tempId 
            ? { ...f, id: directUploadData.signed_id, status: 'uploaded', progress: 100 }
            : f
        ));
        
        uploadedFileIds.push(directUploadData.signed_id);
      } catch (error) {
        console.error('Upload error:', error);
        setFiles(prev => prev.map(f => 
          f.id === tempId 
            ? { ...f, status: 'error', progress: 0 }
            : f
        ));
      }
    }

    if (uploadedFileIds.length > 0) {
      onFilesUploaded(uploadedFileIds);
    }
  };

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const getFileIcon = (filename: string) => {
    const ext = filename.split('.').pop()?.toLowerCase();
    if (ext === 'md' || ext === 'txt') return <FileText className="w-4 h-4" />;
    if (['json', 'yaml', 'yml'].includes(ext ?? '')) return <Code className="w-4 h-4" />;
    return <File className="w-4 h-4" />;
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="space-y-4">
      <Card
        className={`border-2 border-dashed ${
          isDragging ? 'border-blue-500 bg-blue-50 dark:bg-blue-950' : 'border-gray-300 dark:border-gray-600'
        } p-8 text-center transition-colors`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <Upload className="w-12 h-12 mx-auto mb-4 text-gray-400" />
        <p className="text-lg font-medium mb-2">
          Drag and drop files here, or click to select
        </p>
        <p className="text-sm text-gray-500 mb-4">
          Supported formats: {acceptedTypes.join(', ')}
        </p>
        <Button
          onClick={() => {
            const fileInput = document.getElementById('file-input') as HTMLInputElement;
            fileInput?.click();
          }}
          type="button"
        >
          Choose Files
        </Button>
        <input
          type="file"
          multiple
          accept={acceptedTypes.join(',')}
          onChange={handleFileSelect}
          className="hidden"
          id="file-input"
          style={{ display: 'none' }}
        />
      </Card>

      {files.length > 0 && (
        <Card className="p-4">
          <h3 className="font-semibold mb-3">Uploaded Files</h3>
          <div className="space-y-2">
            {files.map(file => (
              <div
                key={file.id}
                className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  {getFileIcon(file.filename)}
                  <div>
                    <p className="font-medium text-sm">{file.filename}</p>
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.size)}
                      {file.status === 'uploading' && ` - ${file.progress}%`}
                      {file.status === 'error' && ' - Upload failed'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeFile(file.id)}
                  disabled={file.status === 'uploading'}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};